import { formatIsoDate, parseIsoDate } from 'helpers/DateHelper';
import { add, sub, differenceInCalendarDays } from 'date-fns';
import strings from 'localization/strings';
import { minimumDateString, maximumDateString } from 'logic/bookingLogic';
import bookingItemTimePeriodPriceReferenceTypes from 'enums/bookingItemTimePeriodPriceReferenceTypes';
import { convertToNumber, formatPercentage, formatAmount, formatNumber } from 'helpers/StringHelper';

const formatAmountOptions = { skipCurrencySuffix: true, skipThousandSeparators: true };
const formatNumberOptions = { skipThousandSeparators: true, sanitizeMinusSign: true };

export const createTimePeriodPrice = (formTimePeriodPrice, appContext) => ({
    id: formTimePeriodPrice.id,
    amount: convertToNumber(formTimePeriodPrice.amount) ?? 0,
    vatRate: (convertToNumber(formTimePeriodPrice.vatRate) ?? 0) / 100,
    commissionRate: (convertToNumber(formTimePeriodPrice.commissionRate) ?? 0) / 100,
    startDate: formatIsoDate(formTimePeriodPrice.startDate, appContext),
    endDate: formatIsoDate(formTimePeriodPrice.endDate, appContext),
    referenceType: formTimePeriodPrice.referenceType,
    referenceId: formTimePeriodPrice.referenceId
});

export const validateTimePeriodPriceCollectionFormData = (timePeriodPrices, appContext, validateFullCoverage = true) => {
    // collection-level validation: check that all dates are covered
    if(timePeriodPrices.length === 0) {
        return strings.noTimePeriodPricesPresent;
    }

    const timePeriods = timePeriodPrices.map(tpp => ({
        startDate: formatIsoDate(tpp.startDate, appContext) ?? minimumDateString,
        endDate: formatIsoDate(tpp.endDate, appContext) ?? maximumDateString,
    }));
    timePeriods.sort((a, b) => {
        if(a.startDate === b.startDate) {
            if(a.endDate === b.endDate) {
                return 0;
            }
            return a.endDate < b.endDate
                ? -1
                : 1;
        }
        return a.startDate < b.startDate
            ? -1
            : 1;
    });

    // first item must start at beginning of time,
    // and last item must end at end of time
    let fullCoverage = timePeriods[0].startDate === minimumDateString &&
        timePeriods[timePeriods.length - 1].endDate === maximumDateString;

    // every single day from the beginning of time to the end of time must be covered
    let maxEndDate = minimumDateString;
    for (let i = 0; i < timePeriods.length - 1; i++) {
        maxEndDate = timePeriods[i].endDate > maxEndDate
            ? timePeriods[i].endDate
            : maxEndDate;
        if(formatIsoDate(add(parseIsoDate(maxEndDate), { days: 1 }), appContext) < timePeriods[i + 1].startDate) {
            fullCoverage = false;
            break;
        }
    }

    if(validateFullCoverage && !fullCoverage) {
        return strings.timePeriodsMustCoverAllPossibleDates;
    }

    return undefined;
};

export const validateTimePeriodPriceFormData = (timePeriodPriceFormData, appContext) => {
    // array-item-level validation
    const today = formatIsoDate(new Date(), appContext);
    const yesterday = formatIsoDate(sub(new Date(), { days: 1 }), appContext);
    const existingItem = timePeriodPriceFormData.initialValue;
    const updatedItem = timePeriodPriceFormData;

    const updatedStartDate = formatIsoDate(updatedItem.startDate, appContext) ?? minimumDateString;
    const updatedEndDate = formatIsoDate(updatedItem.endDate, appContext) ?? maximumDateString;

    if(updatedEndDate < updatedStartDate) {
        return strings.timePeriodEndDateMustNotBeBeforeStartDate;
    }

    if (updatedItem.isNew) {
        // a new item, we can do whatever we like
        return undefined;
    }

    if (!existingItem.inUse) {
        // not in use, we can do whatever we like
        return undefined;
    }

    const existingStartDate = formatIsoDate(existingItem.startDate, appContext) ?? minimumDateString;
    const existingEndDate = formatIsoDate(existingItem.endDate, appContext) ?? maximumDateString;
    const existingStartDateLaterOrEqualThanToday = existingStartDate >= today;
    const updatedStartDateLaterThanOrEqualToToday = updatedStartDate >= today;
    const existingEndDateLaterThanOrEqualToYesterday = existingEndDate >= yesterday;
    const updatedEndDateLaterThanOrEqualToYesterday = updatedEndDate >= yesterday;

    const startDateChanged = updatedStartDate !== existingStartDate;
    const endDateChanged = updatedEndDate !== existingEndDate;
    const otherPropertiesChanged = convertToNumber(updatedItem.amount) !== convertToNumber(existingItem.amount) ||
                                   convertToNumber(updatedItem.vatRate) !== convertToNumber(existingItem.vatRate) ||
                                   convertToNumber(updatedItem.commissionRate) !== convertToNumber(existingItem.commissionRate);

    if (!startDateChanged && !endDateChanged && !otherPropertiesChanged) {
        // nothing changed
        return undefined;
    }

    if (startDateChanged && !(existingStartDateLaterOrEqualThanToday && updatedStartDateLaterThanOrEqualToToday)) {
        // starts today or earlier - change not allowed
        return strings.cantChangeWhenStartDateHasPassed;
    }

    if (endDateChanged && !(existingEndDateLaterThanOrEqualToYesterday && updatedEndDateLaterThanOrEqualToYesterday)) {
        // an end date that is changed from later than or equal to today to earlier than today - change not allowed
        return strings.cantChangeEndDateToPassedDate;
    }

    return undefined;
};


export const getTimePeriodPriceOverlap = (timePeriodPrice, timePeriod, appContext) => {
    const timePeriodPriceStartDate = formatIsoDate(timePeriodPrice.startDate, appContext) ?? minimumDateString;
    const timePeriodPriceEndDate = formatIsoDate(timePeriodPrice.endDate, appContext) ?? maximumDateString;
    const timePeriodStartDate = formatIsoDate(timePeriod.startDate, appContext) ?? minimumDateString;
    const timePeriodEndDate = formatIsoDate(timePeriod.endDate, appContext) ?? maximumDateString;

    if (timePeriodEndDate < timePeriodPriceStartDate ||
        timePeriodStartDate > timePeriodPriceEndDate) {
        return {
            overlaps: false,
            timePeriodPrice
        };
    }
    const overlappingTimePeriod = {
        startDate: timePeriodStartDate > timePeriodPriceStartDate ? timePeriodStartDate : timePeriodPriceStartDate,
        endDate: timePeriodEndDate < timePeriodPriceEndDate ? timePeriodEndDate : timePeriodPriceEndDate
    };
    overlappingTimePeriod.numberOfDays = differenceInCalendarDays(parseIsoDate(overlappingTimePeriod.endDate), parseIsoDate(overlappingTimePeriod.startDate)) + 1;

    return {
        overlaps: true,
        overlappingTimePeriod,
        timePeriodPrice
    };
};

export const timePeriodPriceIsActive = (timePeriodPrice, date, appContext) =>
    (formatIsoDate(timePeriodPrice.startDate, appContext) ?? minimumDateString) <= formatIsoDate(date, appContext) &&
    (formatIsoDate(timePeriodPrice.endDate, appContext) ?? maximumDateString) >= formatIsoDate(date, appContext);

export const createTimePeriodPriceReferenceDictionary = storageSite => {
    const dictionary = {};
    storageSite.storageGroups.forEach(storageGroup => {
        storageGroup.timePeriodPrices.forEach(tpp => {
            dictionary[`${bookingItemTimePeriodPriceReferenceTypes.storageGroupTimePeriodPrice.key}-${tpp.id}`] = tpp;
        });
    });
    storageSite.additionalServices.forEach(additionalService => {
        additionalService.timePeriodPrices.forEach(tpp => {
            dictionary[`${bookingItemTimePeriodPriceReferenceTypes.additionalServiceTimePeriodPrice.key}-${tpp.id}`] = tpp;
        });
    });
    return dictionary;
};

export const getTimePeriodPriceDifferences = (formTimePeriodPrice, referenceTimePeriodPrice, currency, appContext) => {
    if(!referenceTimePeriodPrice) {
        return [];
    }
    const normalizedFormTimePeriodPrice = createTimePeriodPrice(formTimePeriodPrice, appContext);
    const normalizedReferenceTimePeriodPrice = {
        ...referenceTimePeriodPrice,
        startDate: formatIsoDate(referenceTimePeriodPrice.startDate, appContext),
        endDate: formatIsoDate(referenceTimePeriodPrice.endDate, appContext)
    };
    const defaultEqualFunc = (f, r) => f === r;
    const allowNotSetEqualFunc = (f, r) => f === r || f === undefined;
    return [
            { key: 'amount', label: 'Amount', formatter: o => formatAmount(o, currency, appContext), equalFunc: defaultEqualFunc },
            { key: 'vatRate', label: 'VAT rate', formatter: o => formatPercentage(o, appContext), equalFunc: defaultEqualFunc },
            { key: 'commissionRate', label: 'Commission rate', formatter: o => formatPercentage(o, appContext), equalFunc: defaultEqualFunc },
            { key: 'startDate', label: 'Start date', formatter: o => o ?? '-', equalFunc: allowNotSetEqualFunc },
            { key: 'endDate', label: 'End date', formatter: o => o ?? '-', equalFunc: allowNotSetEqualFunc }
        ]
        .filter(({ key, equalFunc }) => !equalFunc(normalizedFormTimePeriodPrice[key], normalizedReferenceTimePeriodPrice[key]))
        .map(({ key, label, formatter }) => ({
            label,
            formValue: normalizedFormTimePeriodPrice[key],
            referenceValue: normalizedReferenceTimePeriodPrice[key],
            formatter
        }));
};

export const createFormDataForTimePeriodPrice = (timePeriodPrice, currency, appContext, populateInitialValue = true) => ({
    id: timePeriodPrice.id,
    amount: formatAmount(timePeriodPrice.amount, currency, appContext, formatAmountOptions),
    eligibleForVat: !!timePeriodPrice.vatRate,
    vatRate: formatNumber(100 * timePeriodPrice.vatRate, appContext, formatNumberOptions), // not editable, just carrying information
    commissionRate: formatNumber(100 * timePeriodPrice.commissionRate, appContext, formatNumberOptions),
    startDate: parseIsoDate(timePeriodPrice.startDate) ?? null,
    endDate: parseIsoDate(timePeriodPrice.endDate) ?? null,
    referenceType: timePeriodPrice.referenceType,
    referenceId: timePeriodPrice.referenceId,
    editable: timePeriodPrice.editable,
    endDateEditable: timePeriodPrice.endDateEditable,
    inUse: timePeriodPrice.inUse,
    initialValue: populateInitialValue
        ? createFormDataForTimePeriodPrice(timePeriodPrice, currency, appContext, false)
        : undefined
});
